import React, { useState, useEffect, useCallback } from 'react';
import { getAuditLog, getAuditLogFilters } from '../api';
import { FiActivity, FiChevronDown, FiChevronUp } from 'react-icons/fi';

const ACTION_BADGE = {
  create: 'badge-success', update: 'badge-info', delete: 'badge-danger',
  login: 'badge-muted', approve: 'badge-success', reject: 'badge-danger',
  checkout: 'badge-gold', import: 'badge-info', export: 'badge-info',
};

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterOptions, setFilterOptions] = useState({ modules: [], actors: [] });
  const [filters, setFilters] = useState({ module: '', actor_id: '', start: '', end: '' });
  const [expanded, setExpanded] = useState(null);
  const limit = 30;

  useEffect(() => {
    getAuditLogFilters().then(res => setFilterOptions(res.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit };
      if (filters.module) params.module = filters.module;
      if (filters.actor_id) params.actor_id = filters.actor_id;
      if (filters.start) params.start = filters.start;
      if (filters.end) params.end = filters.end;
      const res = await getAuditLog(params);
      setEntries(res.data.entries);
      setTotal(res.data.total);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load audit log');
    }
    setLoading(false);
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title"><FiActivity /> Audit Log</h2>
          <p style={{ color: 'var(--text-muted)', margin: '4px 0 0' }}>Every change made across the admin console, with timestamps</p>
        </div>
      </div>

      <div className="form-row" style={{ marginBottom: '16px' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Module</label>
          <select className="form-control" value={filters.module} onChange={e => { setFilters(p => ({ ...p, module: e.target.value })); setPage(1); }}>
            <option value="">All modules</option>
            {filterOptions.modules.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Actor</label>
          <select className="form-control" value={filters.actor_id} onChange={e => { setFilters(p => ({ ...p, actor_id: e.target.value })); setPage(1); }}>
            <option value="">All actors</option>
            {filterOptions.actors.map(a => <option key={`${a.actor_type}-${a.actor_id}`} value={a.actor_id}>{a.actor_name} ({a.actor_type})</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>From</label>
          <input type="date" className="form-control" value={filters.start} onChange={e => { setFilters(p => ({ ...p, start: e.target.value })); setPage(1); }} />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>To</label>
          <input type="date" className="form-control" value={filters.end} onChange={e => { setFilters(p => ({ ...p, end: e.target.value })); setPage(1); }} />
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead><tr><th>When</th><th>Actor</th><th>Action</th><th>Module</th><th>Record</th><th></th></tr></thead>
            <tbody>
              {entries.length === 0 ? (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)' }}>No matching audit entries.</td></tr>
              ) : entries.map(e => (
                <React.Fragment key={e.id}>
                  <tr style={{ cursor: (e.before_data || e.after_data) ? 'pointer' : 'default' }} onClick={() => setExpanded(expanded === e.id ? null : e.id)}>
                    <td>{new Date(e.created_at).toLocaleString('en-PK')}</td>
                    <td>{e.actor_name || '—'} <span className="badge badge-muted" style={{ marginLeft: '4px' }}>{e.actor_type}</span></td>
                    <td><span className={`badge ${ACTION_BADGE[e.action] || 'badge-muted'}`}>{e.action}</span></td>
                    <td>{e.module}</td>
                    <td>{e.record_label || (e.record_id ? `#${e.record_id}` : '—')}</td>
                    <td>{(e.before_data || e.after_data) && (expanded === e.id ? <FiChevronUp /> : <FiChevronDown />)}</td>
                  </tr>
                  {expanded === e.id && (e.before_data || e.after_data) && (
                    <tr>
                      <td colSpan="6" style={{ background: '#f7f8fc' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: e.before_data && e.after_data ? '1fr 1fr' : '1fr', gap: '12px', padding: '10px 4px' }}>
                          {e.before_data && (
                            <div>
                              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase' }}>Before</div>
                              <pre style={{ fontSize: '11.5px', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(e.before_data, null, 2)}</pre>
                            </div>
                          )}
                          {e.after_data && (
                            <div>
                              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase' }}>After</div>
                              <pre style={{ fontSize: '11.5px', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(e.after_data, null, 2)}</pre>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pagination">
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Previous</button>
        <span>Page {page} of {totalPages} ({total} entries)</span>
        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next</button>
      </div>
    </div>
  );
}
